import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import { format } from 'date-fns';
import FileDropZone from './FileDropZone';
import ConfirmDialog from './ConfirmDialog';
import { filesService } from '../../services/filesService';
import { FileEntity } from '../../types';
import { useNotification } from '../../hooks/NotificationContext';
import { usePermissions } from '../../hooks/usePermissions';
import * as P from '../../constants/permissions';

export const DOKUMENT_KATEGORIEN = [
  'Bestellbestätigung',
  'Angebot',
  'Vertrag',
  'NDA',
  'Leistungsnachweis',
  'Sonstiges',
] as const;

export type DokumentKategorie = (typeof DOKUMENT_KATEGORIEN)[number];

interface DokumentenAblageProps {
  kundeId?: number;
  projektId?: number;
  angebotId?: number;
  onFileCountChange?: (count: number) => void;
}

const formatFileSize = (bytes?: number | null) => {
  if (bytes == null) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const DokumentenAblage: React.FC<DokumentenAblageProps> = ({
  kundeId,
  projektId,
  angebotId,
  onFileCountChange,
}) => {
  const { showSuccess, showError } = useNotification();
  const { hasPermission } = usePermissions();
  const canUpload = hasPermission(P.FILES_UPLOAD);
  const canDelete = hasPermission(P.FILES_DELETE);

  const [files, setFiles] = useState<FileEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [kategorie, setKategorie] = useState<DokumentKategorie>('Bestellbestätigung');
  const [fileToDelete, setFileToDelete] = useState<FileEntity | null>(null);

  const loadFiles = useCallback(async () => {
    if (!kundeId && !projektId && !angebotId) return;
    try {
      setLoading(true);
      const data = await filesService.list({
        kunde_id: kundeId,
        projekt_id: projektId,
        angebot_id: angebotId,
      });
      const sorted = [...data].sort((a, b) => {
        const aDate = a.erstellt_am || '';
        const bDate = b.erstellt_am || '';
        return bDate.localeCompare(aDate);
      });
      setFiles(sorted);
    } catch {
      showError('Dokumente konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [kundeId, projektId, angebotId, showError]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  useEffect(() => {
    onFileCountChange?.(files.length);
  }, [files.length, onFileCountChange]);

  const handleFilesDropped = async (dropped: File[]) => {
    if (!canUpload || (!kundeId && !projektId && !angebotId)) return;
    setUploading(true);
    try {
      for (const file of dropped) {
        await filesService.upload(file, kategorie, projektId, undefined, undefined, kundeId, angebotId);
      }
      showSuccess(dropped.length === 1 ? 'Dokument hochgeladen' : `${dropped.length} Dokumente hochgeladen`);
      await loadFiles();
    } catch {
      // Interceptor zeigt den Fehler
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!fileToDelete) return;
    try {
      await filesService.delete(fileToDelete.id);
      showSuccess('Dokument gelöscht');
      setFileToDelete(null);
      await loadFiles();
    } catch {
      setFileToDelete(null);
    }
  };

  return (
    <Box>
      {canUpload && (
        <Box sx={{ mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 220, mb: 1.5 }}>
            <InputLabel>Kategorie</InputLabel>
            <Select
              label="Kategorie"
              value={kategorie}
              onChange={(e) => setKategorie(e.target.value as DokumentKategorie)}
            >
              {DOKUMENT_KATEGORIEN.map((k) => (
                <MenuItem key={k} value={k}>
                  {k}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FileDropZone
            onFilesDropped={handleFilesDropped}
            uploading={uploading}
            data-testid="dokumenten-ablage-dropzone"
          />
        </Box>
      )}

      {loading ? (
        <Box sx={{ py: 1 }}>
          {Array.from({ length: 2 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={40} sx={{ mb: 1 }} />
          ))}
        </Box>
      ) : files.length === 0 ? (
        <Alert severity="info">Noch keine Dokumente</Alert>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Kategorie</TableCell>
                <TableCell>Dateiname</TableCell>
                <TableCell>Größe</TableCell>
                <TableCell>Datum</TableCell>
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {files.map((file) => (
                <TableRow key={file.id} hover>
                  <TableCell>
                    {file.typ ? (
                      <Chip label={file.typ} size="small" variant="outlined" />
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>{file.dateiname}</TableCell>
                  <TableCell>{formatFileSize(file.dateigroesse)}</TableCell>
                  <TableCell>
                    {file.erstellt_am ? format(new Date(file.erstellt_am), 'dd.MM.yyyy') : '-'}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Herunterladen">
                      <IconButton size="small" color="primary" onClick={() => filesService.download(file.id)}>
                        <DownloadIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {canDelete && (
                      <Tooltip title="Löschen">
                        <IconButton size="small" color="error" onClick={() => setFileToDelete(file)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {uploading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
          <CircularProgress size={20} />
        </Box>
      )}

      <ConfirmDialog
        open={Boolean(fileToDelete)}
        title="Dokument löschen"
        message={`Möchten Sie „${fileToDelete?.dateiname || ''}“ wirklich löschen?`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setFileToDelete(null)}
      />
    </Box>
  );
};

export default DokumentenAblage;
